import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { en } from './en.ts';
import { substitute } from './format.ts';
import { DEFAULT_LOCALE, isLocale } from './types.ts';
import type { Locale, TParams } from './types.ts';
import { zhCN } from './zh-CN.ts';

/**
 * React context that carries the active locale + a memoised `t()` over
 * the appropriate dictionary.
 *
 * The provider resolves the initial locale in this priority order:
 *   1. Explicit `initialLocale` prop (used by tests so they render
 *      against a known dictionary without touching `localStorage`).
 *   2. `localStorage` key `console.locale` — persistence across tabs.
 *   3. `DEFAULT_LOCALE` (zh-CN).
 *
 * When the locale flips we also sync `<html lang>` so screen readers
 * and page-translation extensions see the right language tag.
 *
 * The `t()` function closes over the current dictionary via a `useMemo`;
 * switching locales re-keys the memo so every consumer renders with
 * fresh translations without callers needing to do anything.
 */
export type I18nKey = keyof typeof zhCN;

interface I18nContextValue {
  readonly locale: Locale;
  readonly setLocale: (next: Locale) => void;
  readonly t: (key: I18nKey, params?: TParams) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

const STORAGE_KEY = 'console.locale';

type Dictionary = Record<keyof typeof zhCN, string>;

const DICTIONARIES: Readonly<Record<Locale, Dictionary>> = {
  'zh-CN': zhCN,
  en: en,
};

function readStoredLocale(): Locale | null {
  try {
    if (typeof window === 'undefined') {
      return null;
    }
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) {
      return null;
    }
    return isLocale(raw) ? raw : null;
  } catch {
    // localStorage can throw in sandboxed iframes / privacy modes.
    // Treat any error as "no stored preference".
    return null;
  }
}

function writeStoredLocale(next: Locale): void {
  try {
    if (typeof window === 'undefined') {
      return;
    }
    window.localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // Non-fatal — the locale still lives in React state for the
    // current session, it just will not persist across reloads.
  }
}

interface Props {
  readonly initialLocale?: Locale;
  readonly children: ReactNode;
}

export function I18nProvider({ initialLocale, children }: Props): JSX.Element {
  const [locale, setLocaleState] = useState<Locale>(() => {
    if (initialLocale !== undefined) {
      return initialLocale;
    }
    return readStoredLocale() ?? DEFAULT_LOCALE;
  });

  const setLocale = useCallback((next: Locale): void => {
    setLocaleState(next);
    writeStoredLocale(next);
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }
    document.documentElement.lang = locale;
    // Keep tab title and meta description in sync with the active locale.
    // `index.html` ships the zh-CN (default) strings so first paint is
    // correct for most visitors; this effect rewrites both when an
    // en-preferring visitor returns or on any runtime switch. Reads the
    // dictionary directly — `t` is memoised on `locale`, so adding it
    // to the deps would re-run this effect for the same reason `locale`
    // already does.
    const dict = DICTIONARIES[locale];
    document.title = dict['meta.title'];
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription !== null) {
      metaDescription.setAttribute('content', dict['meta.description']);
    }
  }, [locale]);

  const t = useMemo(() => {
    const dict = DICTIONARIES[locale];
    return (key: I18nKey, params?: TParams): string => substitute(dict[key], params);
  }, [locale]);

  const value = useMemo<I18nContextValue>(
    () => ({ locale, setLocale, t }),
    [locale, setLocale, t],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/**
 * Returns the memoised `t()` function for the active locale. Calling it
 * outside an `<I18nProvider>` throws so a misconfigured tree surfaces
 * at render rather than silently rendering keys.
 */
export function useT(): (key: I18nKey, params?: TParams) => string {
  const ctx = useContext(I18nContext);
  if (ctx === null) {
    throw new Error('useT must be used inside <I18nProvider>');
  }
  return ctx.t;
}

/**
 * Separate hook for consumers that need the active locale or the setter
 * (e.g. the header language switcher). Kept split from `useT()` so the
 * vast majority of callers that only translate do not pull `setLocale`
 * into their render scope.
 */
export function useLocale(): { readonly locale: Locale; readonly setLocale: (l: Locale) => void } {
  const ctx = useContext(I18nContext);
  if (ctx === null) {
    throw new Error('useLocale must be used inside <I18nProvider>');
  }
  return { locale: ctx.locale, setLocale: ctx.setLocale };
}
